/**
 * WrapBanner (Welle 6b)
 *
 * Nachbereitungs-Formular für den Berater nach einem Call:
 *  - Fall abgeschlossen? (Ja / Nein)
 *  - Thema (Multi-Select aus 10 Finanz-Topics)
 *  - Gesprächsqualität (Sterne 1-5)
 *
 * Eingaben sind optional. "Speichern" schickt alles in einem POST.
 * "Überspringen" wechselt direkt zurück ins Dashboard ohne POST.
 */

import { useState } from 'react';
import { useUIStore } from '@/state/store';
import { apiFetch } from '@/lib/auth';
import { summaryVerdict } from '@/hooks/useWebRtc';
import type { PeerStats, QualityLevel, CallSummary } from '@/hooks/useWebRtc';

const TOPICS: { key: string; label: string }[] = [
  { key: 'konto', label: 'Konto' },
  { key: 'karte', label: 'Karte' },
  { key: 'online-banking', label: 'Online-Banking' },
  { key: 'ueberweisung', label: 'Überweisung' },
  { key: 'geldanlage', label: 'Geldanlage' },
  { key: 'kredit', label: 'Kredit' },
  { key: 'baufinanzierung', label: 'Baufinanzierung' },
  { key: 'versicherung', label: 'Versicherung' },
  { key: 'beschwerde', label: 'Beschwerde' },
  { key: 'sonstiges', label: 'Sonstiges' },
];

export function WrapBanner() {
  const view = useUIStore((s) => s.view);
  const setView = useUIStore((s) => s.setView);
  const activeCallId = useUIStore((s) => s.activeCallId);
  const summary = useUIStore((s) => s.lastCallSummary);

  const [resolved, setResolved] = useState<boolean | null>(null);
  const [topics, setTopics] = useState<Set<string>>(new Set());
  const [quality, setQuality] = useState<number>(0);
  const [submitting, setSubmitting] = useState(false);

  if (view !== 'wrap') return null;

  function toggleTopic(key: string) {
    setTopics((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  }

  function reset() {
    setResolved(null);
    setTopics(new Set());
    setQuality(0);
  }

  // pre188: Nachbereitung erledigt (Speichern ODER Überspringen) → Status zurück auf
  // 'verfügbar' (Header + Server + Roster der anderen Berater).
  function goAvailable() {
    useUIStore.getState().setStatus('available');
    window.dispatchEvent(new CustomEvent('bamoa:my-status', { detail: { status: 'available' } }));
  }

  async function save() {
    if (!activeCallId) {
      console.warn('[wrap] kein activeCallId, überspringe');
      setView('dashboard');
      reset();
      goAvailable();
      return;
    }
    setSubmitting(true);
    try {
      const body: Record<string, any> = {};
      if (resolved !== null) body.resolved = resolved;
      if (topics.size > 0) body.topics = Array.from(topics);
      if (quality > 0) body.quality = quality;

      const res = await apiFetch(`/api/calls/${activeCallId}/agent-feedback`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      if (!res.ok) {
        console.warn('[wrap] agent-feedback POST failed', res.status);
      }
    } catch (e) {
      console.warn('[wrap] agent-feedback POST error', e);
    } finally {
      setSubmitting(false);
      setView('dashboard');
      reset();
      goAvailable();
    }
  }

  function skip() {
    setView('dashboard');
    reset();
    goAvailable();
  }

  return (
    <div className="wrap-banner show" style={{ alignItems: 'flex-start', padding: '14px 18px' }}>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 10, flex: 1 }}>
        <CallQualitySummaryBlock summary={summary} />
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <strong>Nachbereitung</strong>
          <button
            onClick={skip}
            style={{
              background: 'transparent', border: 'none', color: 'currentColor',
              opacity: 0.6, cursor: 'pointer', fontSize: 13,
            }}
          >
            Überspringen
          </button>
        </div>

        {/* Reihe 1: Fall abgeschlossen */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
          <span style={{ fontSize: 13, opacity: 0.75, minWidth: 130 }}>Fall abgeschlossen?</span>
          <WrapPillButton selected={resolved === true} onClick={() => setResolved(true)}>Ja</WrapPillButton>
          <WrapPillButton selected={resolved === false} onClick={() => setResolved(false)}>Nein</WrapPillButton>
        </div>

        {/* Reihe 2: Themen */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
          <span style={{ fontSize: 13, opacity: 0.75, minWidth: 130 }}>Thema</span>
          <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
            {TOPICS.map((t) => (
              <WrapPillButton key={t.key} selected={topics.has(t.key)} onClick={() => toggleTopic(t.key)}>
                {t.label}
              </WrapPillButton>
            ))}
          </div>
        </div>

        {/* Reihe 3: Qualität */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
          <span style={{ fontSize: 13, opacity: 0.75, minWidth: 130 }}>Gesprächsqualität</span>
          <div style={{ display: 'flex', gap: 4 }}>
            {[1, 2, 3, 4, 5].map((n) => (
              <button
                key={n}
                onClick={() => setQuality(n)}
                style={{
                  background: 'transparent', border: 'none', padding: 2,
                  cursor: 'pointer', lineHeight: 0,
                }}
                aria-label={`${n} Stern${n === 1 ? '' : 'e'}`}
              >
                <svg
                  width="22" height="22" viewBox="0 0 24 24"
                  fill={quality >= n ? '#FFB400' : 'none'}
                  stroke={quality >= n ? '#FFB400' : 'currentColor'}
                  strokeWidth="1.5" strokeLinejoin="round"
                  style={{ opacity: quality >= n ? 1 : 0.55 }}
                >
                  <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z" />
                </svg>
              </button>
            ))}
          </div>
        </div>
      </div>

      <button
        className="wb-action"
        disabled={submitting}
        onClick={save}
        style={{ alignSelf: 'flex-end' }}
      >
        {submitting ? 'Speichere…' : 'Speichern'}
      </button>
    </div>
  );
}

function WrapPillButton({
  selected, onClick, children,
}: { selected: boolean; onClick: () => void; children: React.ReactNode }) {
  return (
    <button
      onClick={onClick}
      style={{
        padding: '4px 12px',
        borderRadius: 14,
        border: '1px solid ' + (selected ? 'currentColor' : 'rgba(255,255,255,0.18)'),
        background: selected ? 'currentColor' : 'transparent',
        color: selected ? '#000' : 'currentColor',
        fontSize: 12,
        cursor: 'pointer',
        fontWeight: selected ? 600 : 400,
        transition: 'all 0.12s ease',
      }}
    >
      {children}
    </button>
  );
}

/**
 * Welle 8c-pre32: Qualitätszusammenfassung des gerade beendeten Gesprächs.
 * Zeigt JE Seite (Berater empfängt Gast, Stele empfängt Berater) die kumulierten
 * Netz-Kennzahlen (RTT ø/Spitze, Jitter max, Verlust, Freezes) plus unsere Kennzahlen
 * (Auflösung/fps/Codec). Dieselben Daten werden server-seitig je Call geloggt.
 */
function CallQualitySummaryBlock({ summary }: { summary: CallSummary | null }) {
  if (!summary || (!summary.agent && !summary.stele)) return null;
  const vCls = summary.worst === 'bad' ? 'bad' : summary.worst === 'fair' ? 'warn' : '';
  const vLabel = summary.worst === 'bad' ? 'Schwach' : summary.worst === 'fair' ? 'Eingeschränkt' : 'Gut';
  const mins = Math.floor(summary.durationSec / 60);
  const secs = summary.durationSec % 60;
  const dur = mins > 0 ? `${mins} min ${secs} s` : `${secs} s`;
  return (
    <div className="wb-summary">
      <div className="wb-summary-head">
        <span className="wb-summary-title">Verbindungsqualität · {dur}</span>
        <span className={`wb-summary-verdict ${vCls}`}><span className="v-dot"></span>{vLabel}</span>
      </div>
      <div className="wb-sum-sides">
        <SummarySide name="Berater ◂ Gast" s={summary.agent} />
        <SummarySide name="Stele ◂ Berater" s={summary.stele} />
      </div>
    </div>
  );
}

function SummarySide({ name, s }: { name: string; s: PeerStats | null }) {
  if (!s) {
    return (
      <div className="wb-sum-side">
        <div className="s-name">{name}</div>
        <div className="s-line"><span className="k">—</span><span>keine Daten</span></div>
      </div>
    );
  }
  const verdict: QualityLevel = summaryVerdict(s);
  const rttCls = verdict === 'bad' ? 'bad' : verdict === 'fair' ? 'warn' : '';
  const lossCls = s.lossPct >= 3 ? 'bad' : s.lossPct >= 1 ? 'warn' : '';
  const freezeCls = s.freezeCount > 0 ? 'warn' : '';
  return (
    <div className="wb-sum-side">
      <div className="s-name">{name}</div>
      <div className="s-line">
        <span className="k">RTT ø/max</span>
        <span className={rttCls}>{s.avgRttMs ?? '—'}/{s.peakRttMs ?? '—'} ms</span>
      </div>
      <div className="s-line">
        <span className="k">Jitter max</span>
        <span>{s.maxJitterMs ?? '—'} ms</span>
      </div>
      <div className="s-line">
        <span className="k">Verlust</span>
        <span className={lossCls}>{s.lossPct}% ({s.packetsLost})</span>
      </div>
      <div className="s-line">
        <span className="k">Freezes</span>
        <span className={freezeCls}>{s.freezeCount} · {s.freezeSeconds}s</span>
      </div>
      <div className="s-our">{s.width}×{s.height} · {s.rxFps}fps · {s.codec}{s.relay ? ' · Relay' : ''}</div>
    </div>
  );
}
